import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Loader2,
  CheckCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "./GetTeamPawsAvailabilityPage.css";
import apiClient from "../../api/apiClient";
import { useGlobalAlert } from "../../context/AlertContext";
import TopNavBar from "../../components/TopNavBar";
import appIcon from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";
import morningIconSvg from "../../assets/morning.svg";
import afternoonIconSvg from "../../assets/afternoon.svg";
import eveningIconSvg from "../../assets/evening.svg";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const GetTeamPawsAvailabilityPage = () => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();

  const location = useLocation();
  const { profile } = useSelector((state) => state.user);

  const stateData = location.state || {};
  const partnerData = stateData.partnerData || null;
  const partnerId = stateData.partnerId || null;
  const dogName = stateData.myDogName || "Dog";
  const partnerName = stateData.partnerName || "Partner";

  const isFromCalendarNav = stateData.fromNavBar;

  const initialRequesterUserId = partnerData?.partnerId || partnerId || null;

  const today = useMemo(() => new Date(), []);

  const [value, setValue] = React.useState(dayjs(today));
  const [message, setMessage] = useState("");
  const [weekId, setWeekId] = useState("");
  const [isBroadcast, setIsBroadcast] = useState(false);

  const [fetchedRequesterId, setFetchedRequesterId] = useState(null);
  const [fetchedDogId, setFetchedDogId] = useState(null);

  const activeRequesterId = initialRequesterUserId || fetchedRequesterId;

  const [availabilityGrid, setAvailabilityGrid] = useState({});
  const [selectedGrid, setSelectedGrid] = useState({});
  const [lockedGrid, setLockedGrid] = useState({});
  const [isWeekLocked, setIsWeekLocked] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [user, setUser] = useState(null);

  const [showNoAvailPopup, setShowNoAvailPopup] = useState(false);
  const [noAvailMessage, setNoAvailMessage] = useState("");

  const requesterDog = user?.dogs?.[0];
  const activeDogId = requesterDog?.id || fetchedDogId;

  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const coreSlots = ["morning", "afternoon", "evening"];

  const fetchAvailability = useCallback(
    async (selectedDate) => {
      if (!profile?.id) return;

      setIsLoadingData(true);
      setShowNoAvailPopup(false);
      const weekStartStr = selectedDate.startOf("week").format("YYYY-MM-DD");
      const currentWeekEndStr = selectedDate.endOf("week").format("YYYY-MM-DD");

      try {
        const [resAvail, resApprovedBookings, resPendingBookings] =
          await Promise.all([
            apiClient
              .get(
                `/availability/getAvailabilityByWalkerAndWeekStart?walker_user_id=${profile.id}&week_start=${weekStartStr}`,
              )
              .catch((err) => ({
                data: {
                  status: false,
                  message:
                    err?.response?.data?.message ||
                    "No availability data retrieved.",
                },
              })),
            apiClient
              .get(
                `/bookingWeeks/get-Approved-Booking-WeeksBy-UserId/${profile.id}`,
              )
              .catch(() => ({ data: { data: [] } })),
            apiClient
              .get(`/bookingWeeks/getBookingWeeksByUserId/${profile.id}`)
              .catch(() => ({ data: { data: [] } })),
          ]);

        let newAvailGrid = {};
        let newLockedGrid = {};
        let newSelectedGrid = {};
        let weekHasBookings = false;

        let dynamicReqId = initialRequesterUserId;
        let dynamicDogId = null;

        if (resAvail.data?.status && resAvail.data.data) {
          const apiData = resAvail.data.data;
          setMessage(apiData.message || "");
          setIsBroadcast(!!apiData.is_broadcast);

          setWeekId(apiData.id || apiData.days?.[0]?.week_id);

          if (!dynamicReqId && apiData.requester_user_id) {
            dynamicReqId = apiData.requester_user_id;
          }
          if (!dynamicDogId && apiData.dog_id) {
            dynamicDogId = apiData.dog_id;
          }

          apiData.days?.forEach((dayEntry) => {
            const dayName = dayEntry.day_of_week;
            if (dayEntry.morning) newAvailGrid[`${dayName}-morning`] = true;
            if (dayEntry.afternoon) newAvailGrid[`${dayName}-afternoon`] = true;
            if (dayEntry.evening) newAvailGrid[`${dayName}-evening`] = true;
            if (dayEntry.full_day) newAvailGrid[`${dayName}-full`] = true;
          });
        } else {
          setMessage("");
          setIsBroadcast(false);
          setNoAvailMessage(
            "No availability request received for this week.",
          );
          setShowNoAvailPopup(true);
        }

        if (!dynamicReqId || !dynamicDogId) {
          const allBookings = [
            ...(resApprovedBookings.data?.data || []),
            ...(resPendingBookings.data?.data || []),
          ];
          const matchingWeek = allBookings.find(
            (w) => dayjs(w.week_end).format("YYYY-MM-DD") === currentWeekEndStr,
          );

          if (matchingWeek) {
            if (!dynamicReqId) {
              dynamicReqId =
                String(matchingWeek.requester_user_id) === String(profile.id)
                  ? matchingWeek.walker_user_id
                  : matchingWeek.requester_user_id;
            }
            if (!dynamicDogId && matchingWeek.dog_id) {
              dynamicDogId = matchingWeek.dog_id;
            }
          }
        }

        if (
          dynamicReqId &&
          String(dynamicReqId) !== String(initialRequesterUserId)
        ) {
          setFetchedRequesterId(dynamicReqId);
        }
        if (dynamicDogId) {
          setFetchedDogId((prev) =>
            prev !== dynamicDogId ? dynamicDogId : prev,
          );
        }

        if (resApprovedBookings.data?.status && resApprovedBookings.data.data) {
          const matchingWeeks = resApprovedBookings.data.data.filter((w) => {
            const wEnd = dayjs(w.week_end).format("YYYY-MM-DD");

            const recordPartnerId =
              String(w.requester_user_id) === String(profile.id)
                ? String(w.walker_user_id)
                : String(w.requester_user_id);

            return (
              wEnd === currentWeekEndStr &&
              (dynamicReqId ? recordPartnerId === String(dynamicReqId) : true)
            );
          });

          if (matchingWeeks.length > 0) {
            weekHasBookings = true;
            matchingWeeks.forEach((matchingWeek) => {
              matchingWeek.days.forEach((dayEntry) => {
                const dayName = dayEntry.day_of_week;
                if (dayEntry.morning) {
                  newLockedGrid[`${dayName}-morning`] = true;
                  newSelectedGrid[`${dayName}-morning`] = true;
                }
                if (dayEntry.afternoon) {
                  newLockedGrid[`${dayName}-afternoon`] = true;
                  newSelectedGrid[`${dayName}-afternoon`] = true;
                }
                if (dayEntry.evening) {
                  newLockedGrid[`${dayName}-evening`] = true;
                  newSelectedGrid[`${dayName}-evening`] = true;
                }
                if (dayEntry.full_day) {
                  newLockedGrid[`${dayName}-full`] = true;
                  newSelectedGrid[`${dayName}-full`] = true;
                }
              });
            });
          }
        }

        if (resPendingBookings.data?.status && resPendingBookings.data.data) {
          const matchingWeeks = resPendingBookings.data.data.filter((w) => {
            const wEnd = dayjs(w.week_end).format("YYYY-MM-DD");

            const recordPartnerId =
              String(w.requester_user_id) === String(profile.id)
                ? String(w.walker_user_id)
                : String(w.requester_user_id);

            return (
              wEnd === currentWeekEndStr &&
              (dynamicReqId ? recordPartnerId === String(dynamicReqId) : true)
            );
          });

          if (matchingWeeks.length > 0) {
            weekHasBookings = true;
            matchingWeeks.forEach((matchingWeek) => {
              matchingWeek.days.forEach((dayEntry) => {
                const dayName = dayEntry.day_of_week;
                if (dayEntry.morning) {
                  newSelectedGrid[`${dayName}-morning`] = true;
                }
                if (dayEntry.afternoon) {
                  newSelectedGrid[`${dayName}-afternoon`] = true;
                }
                if (dayEntry.evening) {
                  newSelectedGrid[`${dayName}-evening`] = true;
                }
                if (dayEntry.full_day) {
                  newSelectedGrid[`${dayName}-full`] = true;
                }
              });
            });
          }
        }

        setAvailabilityGrid(newAvailGrid);
        setLockedGrid(newLockedGrid);
        setSelectedGrid(newSelectedGrid);
        setIsWeekLocked(weekHasBookings);
      } catch (err) {
        console.error("Error fetching availability:", err);
        setAvailabilityGrid({});
        setLockedGrid({});
        setSelectedGrid({});
        setIsWeekLocked(false);
        setNoAvailMessage(
          "Failed to connect to the server to fetch availability.",
        );
        setShowNoAvailPopup(true);
      } finally {
        setIsLoadingData(false);
      }
    },
    [profile?.id, initialRequesterUserId],
  );

  useEffect(() => {
    fetchAvailability(value);
  }, [value, fetchAvailability]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!activeRequesterId) return;
      try {
        const res = await apiClient.get(`/user/details/${activeRequesterId}`);
        if (res.data?.status && res.data.data) setUser(res.data.data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUserDetails();
  }, [activeRequesterId]);

  const renderIcon = (type, size, className = "") => {
    const iconMap = {
      morning: morningIconSvg,
      afternoon: afternoonIconSvg,
      evening: eveningIconSvg,
    };
    return (
      <img
        src={iconMap[type]}
        alt={type}
        className={className}
        style={{ width: size, height: size }}
      />
    );
  };

  const timeSlotsUI = [
    {
      id: "morning",
      label: "MORNING",
      sub: "Before 12 noon",
      icon: renderIcon("morning", 20),
    },
    {
      id: "afternoon",
      label: "AFTERNOON",
      sub: "12 noon- 5 pm",
      icon: renderIcon("afternoon", 20),
    },
    {
      id: "evening",
      label: "EVENING",
      sub: "After 5 pm",
      icon: renderIcon("evening", 20),
    },
    { id: "full", label: "FULL DAY", sub: "+ Sleepover", icon: null },
  ];

  const hasChanges = Object.keys(selectedGrid).some(
    (key) => selectedGrid[key] && !lockedGrid[key],
  );

  const isSubmitDisabled =
    isSubmitting || isLoadingData || isWeekLocked || !hasChanges;

  const toggleCell = (day, slotId) => {
    if (isWeekLocked) return;

    if (slotId === "full") {
      const availableUnlockedSlots = coreSlots.filter(
        (s) => availabilityGrid[`${day}-${s}`] && !lockedGrid[`${day}-${s}`],
      );

      if (availableUnlockedSlots.length === 0) return;

      const isCurrentlyFull = selectedGrid[`${day}-full`];
      const newSelections = { ...selectedGrid };

      if (!isCurrentlyFull) {
        // Toggle everything ON
        newSelections[`${day}-full`] = true;
        availableUnlockedSlots.forEach((s) => {
          newSelections[`${day}-${s}`] = true;
        });
      } else {
        // Toggle everything OFF
        newSelections[`${day}-full`] = false;
        availableUnlockedSlots.forEach((s) => {
          newSelections[`${day}-${s}`] = false;
        });
      }
      setSelectedGrid(newSelections);
    } else {
      const key = `${day}-${slotId}`;
      if (!availabilityGrid[key]) return;
      if (lockedGrid[key]) return;

      setSelectedGrid((prev) => {
        const nextVal = !prev[key];
        const newSelections = { ...prev, [key]: nextVal };
        // If unchecking a core slot, ensure the full day slot is also unchecked
        if (!nextVal) {
          newSelections[`${day}-full`] = false;
        }
        return newSelections;
      });
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || isWeekLocked) return;

    if (!activeRequesterId) {
      triggerAlert("Missing requester ID. Cannot submit booking.", "error");
      return;
    }

    if (!activeDogId) {
      triggerAlert("Missing Dog ID. Cannot submit booking.", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const weekStart = value.startOf("week").format("YYYY-MM-DD");
      const weekEnd = value.endOf("week").format("YYYY-MM-DD");

      const formattedDays = days
        .map((dayLabel, index) => {
          const dateForDay = value
            .startOf("week")
            .add(index, "day")
            .format("YYYY-MM-DD");

          const hasSelection =
            coreSlots.some((s) => selectedGrid[`${dayLabel}-${s}`]) ||
            selectedGrid[`${dayLabel}-full`];

          if (!hasSelection) return null;

          return {
            date: dateForDay,
            day_of_week: dayLabel,
            morning: !!selectedGrid[`${dayLabel}-morning`],
            afternoon: !!selectedGrid[`${dayLabel}-afternoon`],
            evening: !!selectedGrid[`${dayLabel}-evening`],
            full_day: !!selectedGrid[`${dayLabel}-full`],
          };
        })
        .filter(Boolean);

      const payload = {
        requester_user_id: activeRequesterId,
        walker_user_id: profile?.id,
        availability_week_id: weekId,
        dog_id: activeDogId,
        week_start: weekStart,
        week_end: weekEnd,
        message: message,
        days: formattedDays,
      };

      const res = await apiClient.post(
        "/bookingWeeks/createBookingWeek",
        payload,
      );
      if (res.data?.status) {
        triggerAlert("Booking submitted successfully!", "success");
        if (isFromCalendarNav) {
          navigate("/dashboard-switcher");
        } else {
          navigate("/current-bookings");
        }
      }
    } catch (err) {
      console.error("Error creating booking week:", err);
      triggerAlert(err?.response?.data?.message || "Failed to submit", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const years = Array.from({ length: 10 }, (_, i) => dayjs().year() + i);

  const prevWeek = () => setValue(value.subtract(1, "week"));
  const nextWeek = () => setValue(value.add(1, "week"));

  // ACTIVE SLOTS UI LOGIC (For Desktop only)
  const activeDays = useMemo(() => {
    return days
      .map((day, index) => {
        const dateForDay = value.startOf("week").add(index, "day");
        const mAvail = availabilityGrid[`${day}-morning`];
        const aAvail = availabilityGrid[`${day}-afternoon`];
        const eAvail = availabilityGrid[`${day}-evening`];

        if (mAvail || aAvail || eAvail) {
          return {
            day,
            date: dateForDay,
            morning: selectedGrid[`${day}-morning`],
            afternoon: selectedGrid[`${day}-afternoon`],
            evening: selectedGrid[`${day}-evening`],
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [availabilityGrid, selectedGrid, value]);

  const renderSmallSlot = (isSelected, type) => {
    const cellClass = isSelected
      ? "tpa2-small-cell-booked"
      : "tpa2-small-cell-unselected";

    return (
      <div className={`tpa2-small-grid-cell ${cellClass}`}>
        {renderIcon(type, 20, "tpa2-small-icon")}
      </div>
    );
  };

  if (
    isLoadingData &&
    !isWeekLocked &&
    Object.keys(availabilityGrid).length === 0
  ) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="tpa2-container">
      {showNoAvailPopup && (
        <div className="tpa2-popup-overlay">
          <div className="tpa2-popup-content">
            <button
              className="tpa2-popup-close"
              onClick={() => setShowNoAvailPopup(false)}
            >
              <X size={24} />
            </button>
            <h3>Notice</h3>
            <p>{noAvailMessage}</p>
            <button
              className="tpa2-popup-btn"
              onClick={() => setShowNoAvailPopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* DESKTOP HEADER */}
      <div className="tpa2-custom-top-header tpa2-desktop-only">
        <div
          className="tpa2-custom-brand"
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
          <span>Social Paws</span>
        </div>
        <div className="tpa2-header-icons-right">
          <TopNavBar theme="light" />
        </div>
      </div>

      {/* MOBILE HEADER */}
      <div
        className="cb-custom-top-header tpa2-mobile-only"
        style={{ background: "transparent", marginBottom: "-2rem" }}
      >
        <MobileTopBar
          onBack={() => navigate("/dashboard-switcher")}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
        />
        <div className="cb-header-bottom-row">
          <div className="header-icons-right">
            <TopNavBar theme="light" />
          </div>
        </div>
      </div>

      <div className="tpa-scroll-wrapper">
        <div className="tpa2-content">
          <h1 className="tpa2-title">
            {/* {(requesterDog?.dog_name || dogName)?.toUpperCase()}'S AVAILABILITY */}
            Dog Time Availability
          </h1>
          <p className="tpa2-subtitle">
            {/* Choose a time to enjoy Solo Time with{" "}
            {requesterDog?.dog_name || dogName} */}
            Share the times that you’re availabile for walking or spending time
            with a dog this week
          </p>

          <div className="tpa2-top-controls tpa2-hide-on-mobile">
            <div className="tpa2-dropdown-container">
              <select
                value={value.month()}
                onChange={(e) => setValue(value.month(Number(e.target.value)))}
              >
                {months.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="tpa2-dropdown-container">
              <select
                value={value.year()}
                onChange={(e) => setValue(value.year(Number(e.target.value)))}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="tpa2-split-layout tpa2-hide-on-mobile">
            <div className="tpa2-left-col tpa2-planner-container">
              <h3 className="tpa2-planner-title">Weekly-Availability</h3>
              <div className="tpa2-week-nav">
                <button onClick={prevWeek}>
                  <ChevronLeft size={20} />
                </button>
                <span>
                  {value.startOf("week").format("ddd MMM D")} -{" "}
                  {value.endOf("week").format("ddd MMM D")}
                </span>
                <button onClick={nextWeek}>
                  <ChevronRight size={20} />
                </button>
              </div>
              <div className="tpa2-planner-subtitle">Select Slots here</div>

              {/* Grid Header */}
              <div className="tpa2-grid-header-row">
                <div className="tpa2-empty-cell"></div>
                {timeSlotsUI.map((slot) => (
                  <div key={slot.id} className="tpa2-header-slot-label">
                    <span
                      className={
                        slot.id === "full"
                          ? "tpa2-slot-title-last"
                          : "tpa2-slot-title"
                      }
                    >
                      {slot.label}
                    </span>
                    <span
                      className={
                        slot.id === "full"
                          ? "tpa2-slot-sub-last"
                          : "tpa2-slot-sub"
                      }
                    >
                      {slot.sub}
                    </span>
                  </div>
                ))}
              </div>

              {/* Grid Body */}
              <div className="tpa2-grid-body">
                {days.map((day) => {
                  const isDayFullySelected = !!selectedGrid[`${day}-full`];

                  const availableUnlockedCount = coreSlots.filter(
                    (s) =>
                      availabilityGrid[`${day}-${s}`] &&
                      !lockedGrid[`${day}-${s}`],
                  ).length;

                  const isBulkLocked =
                    availableUnlockedCount === 0 && isDayFullySelected;

                  return (
                    <div key={day} className="tpa2-grid-row">
                      <div className="tpa2-day-label">
                        {day},{" "}
                        {value
                          .startOf("week")
                          .add(days.indexOf(day), "day")
                          .format("DD")}
                      </div>

                      {coreSlots.map((slot) => {
                        const key = `${day}-${slot}`;
                        const isAvailable = availabilityGrid[key];
                        const isSelected = selectedGrid[key];
                        const isLocked = lockedGrid[key];

                        let cellClass = "tpa2-grid-cell";
                        if (!isAvailable) {
                          cellClass += " tpa2-cell-unselected";
                        } else if (isLocked) {
                          cellClass += " tpa2-cell-selected tpa2-grid-locked";
                        } else if (isSelected) {
                          cellClass += " tpa2-cell-booked";
                          if (isWeekLocked) cellClass += " tpa2-grid-locked";
                        } else {
                          cellClass += " tpa2-cell-selected";
                        }

                        return (
                          <div
                            key={slot}
                            className={cellClass}
                            style={{
                              opacity: isAvailable ? 1 : 0.4,
                              cursor:
                                !isAvailable || isWeekLocked
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            onClick={() => toggleCell(day, slot)}
                          >
                            {isLocked ? (
                              <CheckCircle
                                size={24}
                                color="#b91c1c"
                                strokeWidth={2}
                              />
                            ) : (
                              <>
                                {slot === "morning" &&
                                  renderIcon("morning", 30, "tpa2-cell-icon")}
                                {slot === "afternoon" &&
                                  renderIcon("afternoon", 30, "tpa2-cell-icon")}
                                {slot === "evening" &&
                                  renderIcon("evening", 30, "tpa2-cell-icon")}
                              </>
                            )}
                          </div>
                        );
                      })}

                      <div
                        className={`tpa2-bulk-checkbox ${isDayFullySelected ? "checked" : ""}`}
                        onClick={() => toggleCell(day, "full")}
                        style={{
                          margin: "0 auto",
                          cursor:
                            isWeekLocked ||
                            isBulkLocked ||
                            availableUnlockedCount === 0
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            isWeekLocked ||
                            isBulkLocked ||
                            availableUnlockedCount === 0
                              ? 0.6
                              : 1,
                        }}
                      >
                        {isDayFullySelected && <Check size={24} />}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="tpa2-bulk-select-row">
                <div className="tpa2-bulk-item">
                  <span>SELECT ALL</span>
                  <div className="tpa2-bulk-checkbox"></div>
                </div>
                <div className="tpa2-bulk-item">
                  <span>WEEKDAYS</span>
                  <div className="tpa2-bulk-checkbox"></div>
                </div>
                <div className="tpa2-bulk-item">
                  <span>WEEKENDS</span>
                  <div className="tpa2-bulk-checkbox"></div>
                </div>
              </div>
            </div>

            <div className="tpa2-right-col">
              <div className="tpa2-right-section">
                <h3 className="tpa2-section-label">Your Active Slots</h3>

                {activeDays.length === 0 ? (
                  <div className="tpa2-no-active-slots">
                    <p>No active slots.</p>
                    <p className="tpa2-hint-text">
                      Select a slot in the week-planner on the left, to make it
                      available.
                    </p>
                  </div>
                ) : (
                  <div className="tpa2-active-slots-container">
                    <div className="tpa2-active-month-chip">
                      {value.format("MMM YYYY")}
                    </div>
                    <div className="tpa2-active-days-wrap">
                      {activeDays.map((d) => (
                        <div key={d.day} className="tpa2-active-day-card">
                          <div className="tpa2-active-day-title">
                            {d.day.charAt(0).toUpperCase() +
                              d.day.slice(1).toLowerCase()}
                            , {d.date.format("DD")}
                          </div>
                          <div className="tpa2-active-day-slots">
                            {renderSmallSlot(d.morning, "morning")}
                            {renderSmallSlot(d.afternoon, "afternoon")}
                            {renderSmallSlot(d.evening, "evening")}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="tpa2-message-section">
                <h3
                  className="tpa2-section-label"
                  style={{ marginTop: "1.5rem" }}
                >
                  Message
                </h3>
                <textarea
                  className="tpa2-message-input"
                  placeholder="Enter here"
                  value={message || "No message provided."}
                  disabled={isWeekLocked}
                />
              </div>
            </div>
          </div>

          <div className="tpa2-mobile-layout tpa2-show-on-mobile">
            <h3 className="tpa2-section-label" style={{ marginBottom: "1rem" }}>
              Active Slots
            </h3>

            <div className="tpa2-mobile-legend">
              {timeSlotsUI.map((slot) => (
                <div key={slot.id} className="tpa2-mobile-legend-item">
                  {slot.id === "full" ? (
                    <div className="tpa2-mobile-legend-checkbox" />
                  ) : (
                    <div className="tpa2-mobile-legend-icon-wrap">
                      {slot.id === "morning" && renderIcon("morning", 20)}
                      {slot.id === "afternoon" && renderIcon("afternoon", 20)}
                      {slot.id === "evening" && renderIcon("evening", 20)}
                    </div>
                  )}
                  <div className="tpa2-mobile-legend-text">
                    <span className="tpa2-mobile-legend-title">
                      {slot.label}
                    </span>
                    <span className="tpa2-mobile-legend-sub">{slot.sub}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="tpa2-mobile-chip-row">
              <div className="tpa2-active-month-chip" style={{ margin: 0 }}>
                {value.format("MMM YYYY")}
              </div>
              {/* <div className="tpa2-mobile-week-nav">
                <button onClick={prevWeek}>
                  <ChevronLeft size={20} />
                </button>
                <button onClick={nextWeek}>
                  <ChevronRight size={20} />
                </button>
              </div> */}
            </div>

            <div className="tpa2-mobile-grid-body">
              {days.map((day) => {
                const isDayFullySelected = !!selectedGrid[`${day}-full`];

                const availableUnlockedCount = coreSlots.filter(
                  (s) =>
                    availabilityGrid[`${day}-${s}`] &&
                    !lockedGrid[`${day}-${s}`],
                ).length;

                const isBulkLocked =
                  availableUnlockedCount === 0 && isDayFullySelected;

                return (
                  <div key={day} className="tpa2-mobile-grid-row">
                    <div className="tpa2-mobile-day-label">
                      {day},{" "}
                      {value
                        .startOf("week")
                        .add(days.indexOf(day), "day")
                        .format("DD")}
                    </div>

                    <div className="tpa2-mobile-slots-container">
                      {coreSlots.map((slot) => {
                        const key = `${day}-${slot}`;
                        const isAvailable = availabilityGrid[key];
                        const isSelected = selectedGrid[key];
                        const isLocked = lockedGrid[key];

                        if (!isAvailable) {
                          return (
                            <div
                              key={slot}
                              className="tpa2-mobile-empty-slot"
                            ></div>
                          );
                        }

                        let cellClass = "tpa2-mobile-grid-cell";
                        if (isLocked) {
                          cellClass += " tpa2-mobile-cell-locked";
                        } else if (isSelected) {
                          cellClass += " tpa2-mobile-cell-selected";
                        } else {
                          cellClass += " tpa2-mobile-cell-available";
                        }

                        return (
                          <div
                            key={slot}
                            className={cellClass}
                            style={{
                              opacity: isLocked || isWeekLocked ? 0.9 : 1,
                              cursor:
                                isLocked || isWeekLocked
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            onClick={() => toggleCell(day, slot)}
                          >
                            {isLocked ? (
                              <CheckCircle
                                size={22}
                                color="#b91c1c"
                                strokeWidth={2}
                              />
                            ) : (
                              <>
                                {slot === "morning" &&
                                  renderIcon("morning", 22, "tpa2-mobile-icon")}
                                {slot === "afternoon" &&
                                  renderIcon(
                                    "afternoon",
                                    22,
                                    "tpa2-mobile-icon",
                                  )}
                                {slot === "evening" &&
                                  renderIcon("evening", 22, "tpa2-mobile-icon")}
                              </>
                            )}
                          </div>
                        );
                      })}

                      <div
                        className={`tpa2-mobile-bulk-checkbox ${isDayFullySelected ? "checked" : ""}`}
                        onClick={() => toggleCell(day, "full")}
                        style={{
                          cursor:
                            isWeekLocked ||
                            isBulkLocked ||
                            availableUnlockedCount === 0
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            isWeekLocked ||
                            isBulkLocked ||
                            availableUnlockedCount === 0
                              ? 0.6
                              : 1,
                        }}
                      >
                        {isDayFullySelected && <Check size={18} />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              className="tpa2-message-section"
              style={{ marginTop: "1.5rem", width: "100%" }}
            >
              <h3 className="tpa2-section-label">Walker's Message</h3>
              <textarea
                className="tpa2-message-input"
                placeholder="Enter here"
                value={message || "No message provided."}
                disabled={isWeekLocked}
              />
            </div>
          </div>

          <div className="tpa2-footer-section">
            {!isWeekLocked && (
              <button
                className="tpa2-submit-btn"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                style={{
                  opacity: isSubmitDisabled ? 0.6 : 1,
                  cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                }}
              >
                {isSubmitting ? <Loader2 className="animate-spin" /> : "SUBMIT"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetTeamPawsAvailabilityPage;
